'use client';

import { api, useQuery } from '@/lib/convex';
import { play, type SoundName } from 'cuelume';
import { useReducedMotion } from 'motion/react';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useWebHaptics } from 'web-haptics/react';

export type HapticIntent = 'tap' | 'success' | 'error' | 'destructive';
export type HapticTriggerOptions = {
  force?: boolean;
  strength?: 'light' | 'medium';
};
export type HapticTrigger = (
  intent: HapticIntent,
  options?: HapticTriggerOptions
) => void;
type AppHapticsState = {
  isEnabled: boolean;
  profileHapticsEnabled: boolean | undefined;
  trigger: HapticTrigger;
};

const HAPTIC_PATTERNS: Record<
  HapticIntent,
  'light' | 'success' | 'error' | 'warning' | 'buzz'
> = {
  tap: 'light',
  success: 'success',
  error: 'error',
  destructive: 'buzz',
};

const SOUNDS_BY_INTENT: Record<HapticIntent, SoundName> = {
  tap: 'press',
  success: 'success',
  error: 'droplet',
  destructive: 'toggle',
};

const AppHapticsContext = createContext<AppHapticsState | null>(null);
const HAPTICS_LOG_PREFIX = '[haptics]';

function useAppHapticsState(): AppHapticsState {
  const { trigger: rawTrigger, isSupported } = useWebHaptics();
  const prefersReducedMotion = useReducedMotion();
  const { data: profileHapticsEnabled, isSuccess: hasLoadedPreference } =
    useQuery(api.users.queries.getHapticsPreference);
  const isAndroid = useMemo(
    () =>
      typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent),
    []
  );

  const canFire = !prefersReducedMotion;
  const isEnabled =
    canFire && hasLoadedPreference && profileHapticsEnabled === true;

  useEffect(() => {
    console.info(`${HAPTICS_LOG_PREFIX} state`, {
      isSupported,
      isAndroid,
      prefersReducedMotion,
      hasLoadedPreference,
      profileHapticsEnabled,
      canFire,
      isEnabled,
    });
  }, [
    canFire,
    hasLoadedPreference,
    isAndroid,
    isEnabled,
    isSupported,
    prefersReducedMotion,
    profileHapticsEnabled,
  ]);

  const trigger: HapticTrigger = useCallback(
    (intent: HapticIntent, options?: HapticTriggerOptions) => {
      const shouldFire = canFire && (options?.force || isEnabled);
      if (!shouldFire) {
        console.info(`${HAPTICS_LOG_PREFIX} blocked`, {
          intent,
          force: options?.force ?? false,
          canFire,
          isEnabled,
          isSupported,
          isAndroid,
          prefersReducedMotion,
          hasLoadedPreference,
          profileHapticsEnabled,
        });
        return;
      }

      const pattern =
        intent === 'tap' && options?.strength === 'medium'
          ? 'medium'
          : HAPTIC_PATTERNS[intent];

      console.info(`${HAPTICS_LOG_PREFIX} trigger`, {
        intent,
        pattern,
        sound: SOUNDS_BY_INTENT[intent],
        isSupported,
      });

      play(SOUNDS_BY_INTENT[intent]);

      if (isSupported) {
        const maybePromise = rawTrigger(pattern);
        if (maybePromise && typeof maybePromise.then === 'function') {
          void maybePromise.catch(() => {
            // Ignore device/runtime failures.
            console.info(`${HAPTICS_LOG_PREFIX} trigger-error`, { intent });
          });
        }
      }
    },
    [
      canFire,
      hasLoadedPreference,
      isAndroid,
      isEnabled,
      isSupported,
      prefersReducedMotion,
      profileHapticsEnabled,
      rawTrigger,
    ]
  );

  const resolvedProfilePreference = hasLoadedPreference
    ? profileHapticsEnabled === true
    : undefined;

  return useMemo(
    () => ({
      isEnabled,
      profileHapticsEnabled: resolvedProfilePreference,
      trigger,
    }),
    [isEnabled, resolvedProfilePreference, trigger]
  );
}

export function AppHapticsProvider({ children }: { children: ReactNode }) {
  const value = useAppHapticsState();
  return (
    <AppHapticsContext.Provider value={value}>
      {children}
    </AppHapticsContext.Provider>
  );
}

export function useAppHaptics() {
  const context = useContext(AppHapticsContext);
  if (!context) {
    throw new Error('useAppHaptics must be used within AppHapticsProvider');
  }

  return {
    isEnabled: context.isEnabled,
    profileHapticsEnabled: context.profileHapticsEnabled,
    trigger: context.trigger,
  };
}
